
import { useState } from "react"
import { AssessmentForm, type AssessmentFormData } from "./business-assessment/AssessmentForm"
import { useAssessment } from "./business-assessment/useAssessment"
import { Button } from "./ui/button"
import { ArrowLeft, Edit, LogOut } from "lucide-react"
import { DeleteAssessmentDialog } from "./business-assessment/DeleteAssessmentDialog"
import { supabase } from "@/integrations/supabase/client"
import { useNavigate } from "react-router-dom"

interface BusinessAssessmentProps {
  onComplete?: () => void
}

export function BusinessAssessment({ onComplete }: BusinessAssessmentProps) {
  const [isEditing, setIsEditing] = useState(false)
  const navigate = useNavigate()
  const {
    isLoading,
    defaultValues,
    handleSubmit,
    handleDelete,
    assessmentId,
  } = useAssessment(onComplete)

  const handleBackClick = () => {
    if (onComplete) {
      console.log("Completing assessment view...")
      onComplete()
    }
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    navigate("/")
  }

  const handleFormSubmit = async (values: AssessmentFormData) => {
    await handleSubmit(values)
    setIsEditing(false)
  }

  return (
    <div className="space-y-4 p-2 sm:p-4 max-w-3xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleBackClick} size="sm">
            <ArrowLeft className="h-4 w-4" />
            <span className="ml-2">Back</span>
          </Button>
          <h2 className="text-xl sm:text-2xl font-bold">Business Assessment</h2>
        </div>
        <div className="flex items-center gap-2">
          {assessmentId && !isEditing && (
            <Button 
              variant="outline" 
              onClick={() => setIsEditing(true)}
              className="gap-2"
              size="sm"
            >
              <Edit className="h-4 w-4" />
              Edit Assessment
            </Button>
          )}
          {assessmentId && (
            <DeleteAssessmentDialog onDelete={handleDelete} />
          )}
          <Button 
            variant="outline" 
            onClick={handleSignOut}
            className="gap-2"
            size="sm"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </div>

      <AssessmentForm
        defaultValues={defaultValues}
        isLoading={isLoading}
        onSubmit={handleFormSubmit}
        readOnly={assessmentId !== null && !isEditing}
      />
    </div>
  )
}
